import { useCallback, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  setQuery,
  clearSearch,
  fetchSuggestionsAsync,
  performFullSearchAsync,
  selectSearchQuery,
  selectSuggestions,
  selectLoadingSuggestions,
  selectFullResults,
  selectLoadingFull,
  selectSearchError,
} from "@/store/redux/slices/search/search_slice";

const createDebounce = (fn, delay) => {
  let timer;
  return (...args) => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => fn(...args), delay);
  };
};

export const useGlobalSearch = ({ organizationId } = {}) => {
  const dispatch = useDispatch();

  const query = useSelector(selectSearchQuery);
  const suggestions = useSelector(selectSuggestions);
  const loadingSuggestions = useSelector(selectLoadingSuggestions);
  const fullResults = useSelector(selectFullResults);
  const loadingFull = useSelector(selectLoadingFull);
  const error = useSelector(selectSearchError);

  const suggestionAbortRef = useRef(null);

  const dispatchSuggestions = useCallback(
    (currentQuery) => {
      const trimmed = currentQuery?.trim();

      if (!trimmed) {
        dispatch(clearSearch());
        return;
      }

      if (suggestionAbortRef.current) {
        suggestionAbortRef.current.abort();
      }
      const controller = new AbortController();
      suggestionAbortRef.current = controller;

      dispatch(
        fetchSuggestionsAsync(trimmed, { signal: controller.signal })
      );
    },
    [dispatch]
  );

  const debouncedDispatch = useRef(createDebounce(dispatchSuggestions, 300)).current;

  useEffect(() => {
    debouncedDispatch(query);
  }, [query, debouncedDispatch]);

  const setQueryValue = useCallback(
    (value) => {
      dispatch(setQuery(value));
    },
    [dispatch]
  );

  const performFullSearch = useCallback(
    async (overrideQuery) => {
      const searchQuery = (overrideQuery ?? query)?.trim();
      if (!searchQuery) return null;

      const result = await dispatch(
        performFullSearchAsync({ query: searchQuery, organizationId })
      ).unwrap();

      return result;
    },
    [dispatch, organizationId, query]
  );

  const resetSearch = useCallback(() => {
    dispatch(clearSearch());
  }, [dispatch]);

  return {
    query,
    setQuery: setQueryValue,
    suggestions,
    loadingSuggestions,
    fullResults,
    loadingFull,
    error,
    performFullSearch,
    resetSearch,
  };
};
